/**
 * Roles whose value is COMPUTED for an object rather than stored on it.
 *
 * Decision 0030, which exists because decision 0028's compatibility read was
 * built one layer too high and the layer showed:
 *
 * > *"A page-side value patch such as `withProjectedBodies` is also too late
 * > when the page has already derived a shape without the role."*
 *
 * A migrated note keeps its prose as canonical blocks. Once its legacy `body`
 * scalar is retired, nothing on the object carries that role — so
 * `deriveShape` stops reporting it, the generated lens has no `body` field, and
 * the document canvas has nowhere to mount. Measured in a browser: the reader's
 * canvas painted `""` and no canonical blocks reached the screen.
 *
 * ## Why this is ONE read that returns both, and not two calls
 *
 * 0030: *"Shape derivation and value projection for that operation observe the
 * same revision and the same provider set."*
 *
 * That is the requirement a store FACADE cannot meet by overriding `projection`
 * and `deriveShape` separately: each opens its own coherent read, so a caller
 * that asks for both gets two revisions and a shape that can disagree with the
 * values beside it. @Codex's boundary returns shape, identities and values from
 * one `store.read`, which is why this is its design and not the facade.
 *
 * ## The boundary, which is the whole of 0030
 *
 * > *"The generic store core may know that a role can be derived. It must **not
 * > know what a document is**."*
 *
 * Nothing in this file mentions documents, blocks, `body`, or migration. The
 * document provider lives in `document-derived-role.ts`, where document
 * vocabulary already is.
 */
import type { Role, RoleKind } from '@morph/spec';
import type { RoleValues } from '@morph/runtime';
import type { ObjectId } from './ids.js';
import type { StoredObject } from './model.js';
import type { ObjectReader } from './read.js';
import type { Page, Scope, Slice } from './scope.js';
import type { ShapeRole, StoredShape } from './shape.js';
import type { ObjectStore } from './store.js';

export interface DerivedRoleValue {
  readonly role: Role;
  readonly kind: RoleKind;
  readonly value: unknown;
}

/** A module-owned adapter invoked inside the store's coherent read boundary. */
export interface DerivedRoleProvider {
  /** Cheap composition-time gate; returning false must perform no object reads. */
  supports?(scope: Scope): boolean;
  derive(input: {
    readonly reader: ObjectReader;
    readonly object: StoredObject;
    readonly scope: Scope;
  }): Promise<readonly DerivedRoleValue[]>;
}

export interface EffectiveScopeRead {
  readonly shape: StoredShape;
  readonly objects: Slice<StoredObject>;
  readonly projection: Slice<RoleValues>;
  /** Per-object provenance. Derived roles are readable but not scalar-writable. */
  readonly derived: ReadonlyMap<ObjectId, ReadonlySet<Role>>;
  readonly revision: number;
}

export interface DerivedRoleReadBoundary {
  read(scope: Scope, page: Page,
    options?: { readonly expandRefs?: 0 | 1 | 2 | 3 | 4 }): Promise<EffectiveScopeRead>;
}

const present = (value: unknown): boolean => value !== undefined && value !== null;

/** Compose stored and module-derived roles into one coherent shape/value read. */
export function derivedRoleReadBoundary(
  store: ObjectStore, providers: readonly DerivedRoleProvider[],
): DerivedRoleReadBoundary {
  return {
    read: (scope, page, options = {}) => store.read(async ({ reader, revision }) => {
      const active = providers.filter((provider) => provider.supports?.(scope) !== false);
      if (active.length === 0) {
        const [shape, objects, projection] = await Promise.all([
          store.deriveShape(scope), reader.query(scope, page),
          reader.projection(scope, page, options),
        ]);
        return { shape, objects, projection, derived: new Map(), revision };
      }
      const rawShape = await store.deriveShape(scope);

      /**
       * THE DENOMINATOR, and the one thing a sweep here gets silently wrong.
       *
       * `StoredShape` carries `n` — *"denominator actually used"* — beside
       * `total`, and `ShapeRole.coverage` is over `n`. On a SAMPLED scope those
       * differ, so counting a numerator across every object in the scope and
       * dividing it by `n` is a ratio of two different populations. Measured
       * with a scope sampled to one object and a provider answering for three:
       *
       *     expected 3 to be less than or equal to 1
       *
       * A coverage of 3 for a role at most one object can carry.
       *
       * Asking for `rawShape.n` objects is not an approximation of the fix, it
       * IS the fix: `deriveShape` samples by `members(scope).slice(0, maxSample)`
       * and `query` paginates the same member list, so this is exactly the set
       * that produced `rawShape`. It is also strictly cheaper than sweeping the
       * whole scope, which matters because a provider does real work per object.
       */
      const counting: Page = { limit: rawShape.n };
      const [allObjects, allProjection, pageObjects, pageProjection] = await Promise.all([
        reader.query(scope, counting),
        reader.projection(scope, counting, options),
        reader.query(scope, page),
        reader.projection(scope, page, options),
      ]);
      const answers = new Map<ObjectId, readonly DerivedRoleValue[]>();

      await Promise.all(allObjects.items.map(async (object) => {
        const values = (await Promise.all(active.map((provider) =>
          provider.derive({ reader, object, scope })))).flat();
        if (values.length) answers.set(object.id, values);
      }));

      const roleKinds = new Map<Role, RoleKind>();
      const objectsByRole = new Map<Role, Set<ObjectId>>();
      allObjects.items.forEach((object, index) => {
        const projected = (allProjection.items[index] ?? {}) as Record<string, unknown>;
        for (const role of Object.keys(projected) as Role[]) {
          if (!present(projected[role])) continue;
          const ids = objectsByRole.get(role) ?? new Set<ObjectId>();
          ids.add(object.id);
          objectsByRole.set(role, ids);
        }
        for (const answer of answers.get(object.id) ?? []) {
          roleKinds.set(answer.role, answer.kind);
          const ids = objectsByRole.get(answer.role) ?? new Set<ObjectId>();
          ids.add(object.id);
          objectsByRole.set(answer.role, ids);
        }
      });

      const rawByRole = new Map(rawShape.roles.map((role) => [role.role, role]));
      const roles: ShapeRole[] = [...new Set([...rawByRole.keys(), ...roleKinds.keys()])]
        .map((role) => {
          const raw = rawByRole.get(role);
          return {
            ...(raw ?? { role, kind: roleKinds.get(role)!, keyCount: 0 }),
            coverage: rawShape.n === 0 ? 0 : (objectsByRole.get(role)?.size ?? 0) / rawShape.n,
            ...(roleKinds.has(role) ? { source: 'derived' as const } : {}),
          };
        });
      const shape: StoredShape = { ...rawShape, roles };
      const pageValues = pageProjection.items.map((stored, index) => {
        const object = pageObjects.items[index];
        if (!object) return stored;
        return (answers.get(object.id) ?? []).reduce<RoleValues>((values, answer) =>
          ({ ...values, [answer.role]: answer.value }), stored);
      });
      const derived = new Map(pageObjects.items.map((object) => [object.id,
        new Set((answers.get(object.id) ?? []).map((answer) => answer.role))]));
      return {
        shape,
        objects: pageObjects,
        projection: { items: pageValues,
          ...(pageProjection.cursor === undefined ? {} : { cursor: pageProjection.cursor }),
          ...(pageProjection.total === undefined ? {} : { total: pageProjection.total }) },
        derived,
        revision,
      };
    }),
  };
}
